import { BeaconTableHeader } from '@trp-beacon-ui-kit/foundation/components';
import { beaconSystemBreakpointMedium } from '@trp-beacon-ui-kit/foundation/style-tokens/js/variables';

import {
  Product,
  SortDirection,
} from '../../../product-center-table-wrapper/product-center-table-wrapper.model';
import { Constants as ProductCenterTableWrapperConstants } from './../../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { getNumericValueFromPx } from '../../../../common/utils/formatNumber';

interface SortCategory {
  primarySort: string;
  secondarySort: string;
  tertiarySort: string;
}

/**
 * Sorts the product data cells in a table based on the provided criteria.
 *
 * @param {BeaconTableHeader} tableHeaderEl - The table header element that contains the sort criteria.
 * @param {ProductCenterExhibit} filteredProducts - The component object that contains the data to be sorted.
 * @param {string} productHeaderDropdownValue - The value of the product header dropdown to sort by.
 * @returns {ProductCenterExhibit} - The component object with the sorted data cells.
 */
export const sortProductDataCells = (
  tableHeaderEl: BeaconTableHeader,
  filteredProducts: Product[],
  productHeaderDropdownValue: string,
) => {
  if (tableHeaderEl && filteredProducts && productHeaderDropdownValue) {
    try {
      const sortCategory = getSortCategory(productHeaderDropdownValue);

      // Separate filteredProducts into two arrays: one with values and one without
      let filteredProductsWithValues = filteredProducts.filter((product) => {
        return product.productName[`${productHeaderDropdownValue}`];
      });

      let filteredProductsWithNoValues = filteredProducts.filter((product) => {
        return !product.productName[`${productHeaderDropdownValue}`];
      });

      // Apply primary sorting based on productHeaderDropdownValue
      filteredProductsWithValues?.sort((a, b) => {
        const aValue = a.productName[`${sortCategory.primarySort}`];
        const bValue = b.productName[`${sortCategory.primarySort}`];
        return implementCustomSort(aValue, bValue, tableHeaderEl.sortDirection);
      });

      // Apply custom secondary and tertiary sorting (if needed)
      filteredProductsWithValues = applyCustomSecondaryAndTertiarySort(
        filteredProductsWithValues,
        sortCategory,
        tableHeaderEl.sortDirection,
      );
      filteredProductsWithNoValues = applyCustomSecondaryAndTertiarySort(
        filteredProductsWithNoValues,
        sortCategory,
        tableHeaderEl.sortDirection,
      );

      // Concatenate the two filteredProducts array after sorting
      filteredProducts = filteredProductsWithValues.concat(
        filteredProductsWithNoValues,
      );
    } catch (error) {
      // If something goes wrong, then return the initial filteredProducts
      console.warn(error);
      return filteredProducts;
    }
  }
  return filteredProducts;
};

/**
 * Sorts the data cells in a table based on the provided criteria.
 *
 * @param {BeaconTableHeader} tableHeaderEl - The table header element that contains the sort criteria.
 * @param {ProductCenterExhibit} filteredProducts - The component object that contains the data to be sorted.
 * @param {string} columnId - The ID of the column to sort by.
 * @returns {ProductCenterExhibit} - The component object with the sorted data cells.
 */
export const sortDataCells = (
  tableHeaderEl: BeaconTableHeader,
  filteredProducts: Product[],
  columnId: string,
) => {
  if (tableHeaderEl && filteredProducts && columnId) {
    const arrayPathForKey: string[] = findPath(filteredProducts, columnId);
    arrayPathForKey.shift();
    const keysPath = `${arrayPathForKey.join('.')}`;

    const filteredProductsWithNoValues = filteredProducts
      .filter((product) => {
        const pathForValue = getPath(product, keysPath);
        return typeof pathForValue === 'object'
          ? !pathForValue?.[0]?.value
          : !pathForValue;
      })
      .sort((a, b) => {
        return implementCustomSort(
          a.productName.fundName,
          b.productName.fundName,
          SortDirection.ASCENDING,
        );
      });

    const filteredProductsWithValues = filteredProducts.filter((product) => {
      const pathForValue = getPath(product, keysPath);
      return typeof pathForValue === 'object'
        ? pathForValue?.[0]?.value
        : pathForValue;
    });

    filteredProductsWithValues?.sort((a, b) => {
      const pathForAValue = getPath(a, keysPath);
      const aValue = pathForAValue?.[0]?.value
        ? pathForAValue?.[0]?.value
        : pathForAValue;

      const pathForBValue = getPath(b, keysPath);
      const bValue = pathForBValue?.[0]?.value
        ? pathForBValue?.[0]?.value
        : pathForBValue;

      return implementCustomSort(aValue, bValue, tableHeaderEl.sortDirection);
    });

    filteredProducts = filteredProductsWithValues.concat(
      filteredProductsWithNoValues,
    );
  }
  return filteredProducts;
};

/**
 * Implements a custom sort function to handle different data types.
 */
export const implementCustomSort = (a, b, sortDirection) => {
  if (a === b) {
    return 0;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return sortDirection === SortDirection.ASCENDING ? a - b : b - a;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return sortDirection === SortDirection.ASCENDING
      ? a.localeCompare(b)
      : b.localeCompare(a);
  }
  return 0;
};

/**
 * Groups filtered products by a specified sort key.
 *
 * @param {Product[]} filteredProducts - The array of filtered products to group.
 * @param {string} sortKey - The key to group the products by.
 * @returns {Record<string, Product[]>} - An object where each key is a unique value of the sortKey,
 * and the value is an array of products that share that key.
 */
export const groupFilteredProductsBySortKey = (
  filteredProducts: Product[],
  sortKey: string,
) => {
  return filteredProducts.reduce((accumulator, currentItem) => {
    const groupKey = currentItem.productName[sortKey];
    if (!accumulator[groupKey]) {
      accumulator[groupKey] = [];
    }
    accumulator[groupKey].push(currentItem);
    return accumulator;
  }, {});
};

/**
 * Applies secondary and tertiary sorting to the filtered products categories (with or without values).
 *
 * @param {Product[]} filteredProducts - The array of filtered products to sort.
 * @param {SortCategory} sortCategory - The category object containing primary, secondary, and tertiary sort keys.
 * @returns {Product[]} - The sorted array of filtered products.
 */
export const applyCustomSecondaryAndTertiarySort = (
  filteredProducts: Product[],
  sortCategory: SortCategory,
  sortDirection: string,
) => {
  if (!sortCategory.secondarySort) {
    return filteredProducts;
  }

  const groupedProductsBySecondarySort = groupFilteredProductsBySortKey(
    filteredProducts,
    sortCategory.primarySort,
  );

  for (const key in groupedProductsBySecondarySort) {
    groupedProductsBySecondarySort[key].sort((a, b) => {
      const aValue = a.productName[`${sortCategory.secondarySort}`];
      const bValue = b.productName[`${sortCategory.secondarySort}`];
      return implementCustomSort(aValue, bValue, sortDirection);
    });
  }

  if (sortCategory.tertiarySort) {
    for (const key in groupedProductsBySecondarySort) {
      const groupedProductsByTertiarySort = groupFilteredProductsBySortKey(
        groupedProductsBySecondarySort[key],
        sortCategory.secondarySort,
      );

      for (const tertiaryKey in groupedProductsByTertiarySort) {
        if (groupedProductsByTertiarySort[tertiaryKey].length > 1) {
          groupedProductsByTertiarySort[tertiaryKey].sort((a, b) => {
            const aValue = a.productName[`${sortCategory.tertiarySort}`];
            const bValue = b.productName[`${sortCategory.tertiarySort}`];
            return implementCustomSort(aValue, bValue, sortDirection);
          });
        }
      }

      // Concatenate the sorted arrays back into the secondary sort group
      groupedProductsBySecondarySort[key] = Object.values(
        groupedProductsByTertiarySort,
      ).flat();
    }
  }

  // Concatenate the sorted arrays back into the main array (primaryt sort group)
  filteredProducts = Object.values(
    groupedProductsBySecondarySort,
  ).flat() as Product[];
  return filteredProducts;
};

/**
 * Finds a path in a data structure based on the provided criteria.
 *
 * @param {Object} obj - The data structure to search within.
 * @param {string} key - The key to search for in the data structure.
 * @returns {Array<string>} - An array representing the path to the matched value.
 *
 * @example
 * const data = {
 *   name: 'root',
 *   children: [
 *     {
 *       name: 'child1',
 *       grandchildren: [
 *         { grandchildrenName: 'grandchild1' },
 *       ]
 *     },
 *     { name: 'child2' }
 *   ]
 * };
 * const path = findPath(data, 'grandchildrenName');
 * console.log(path);
 * // Output: ['children', 'grandchildren', 'grandchildrenName']
 */
export const findPath = (obj: object, key: string): string[] => {
  if (!obj || typeof obj !== 'object') {
    return [];
  }
  if (key in obj) {
    return [key];
  }

  for (const prop in obj) {
    if (typeof obj[prop] === 'object') {
      const path = findPath(obj[prop], key);
      if (path.length > 0) {
        return [prop, ...path];
      }
    }
  }
  return [];
};

/**
 * Retrieves a value from an object based on a dot-separated path string.
 *
 * @param {object} obj - The object to retrieve the value from.
 * @param {string} path - The dot-separated path string representing the key sequence.
 * @returns {any} - The value found at the specified path, or undefined if the path does not exist.
 *
 * @example
 * const data = {
 *   user: {
 *     profile: {
 *       name: 'John Doe'
 *     }
 *   }
 * };
 * const value = getPath(data, 'user.profile.name');
 * console.log(value); // Output: 'John Doe'
 */
export const getPath = (obj: object, path: string): any => {
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return undefined;
    }
  }

  return current;
};

/**
 * Determines the sorting order (primary, secondary and tertiary) based on the provided product header dropdown value.
 *
 * @param {string} productHeaderDropdownValue - The value of the product header dropdown selected by the user.
 * @returns {SortCategory} - An object containing primary, secondary, and tertiary sort keys based on business rules.
 */
export const getSortCategory = (
  productHeaderDropdownValue: string,
): SortCategory => {
  const primarySort = productHeaderDropdownValue;
  let secondarySort;
  let tertiarySort;

  switch (productHeaderDropdownValue) {
    case ProductCenterTableWrapperConstants.FUND_FIELD.SHARECLASS:
      secondarySort = ProductCenterTableWrapperConstants.FUND_FIELD.FUNDNAME;
      tertiarySort =
        ProductCenterTableWrapperConstants.FUND_FIELD.BASECURRENCYNAME;
      break;
    case ProductCenterTableWrapperConstants.FUND_FIELD.FUNDNAME:
      secondarySort = ProductCenterTableWrapperConstants.FUND_FIELD.SHARECLASS;
      tertiarySort =
        ProductCenterTableWrapperConstants.FUND_FIELD.BASECURRENCYNAME;
      break;
    case ProductCenterTableWrapperConstants.FUND_FIELD.BASECURRENCYNAME:
    case ProductCenterTableWrapperConstants.FUND_FIELD.VEHICLETYPENAME:
    default:
      secondarySort = ProductCenterTableWrapperConstants.FUND_FIELD.FUNDNAME;
      tertiarySort = ProductCenterTableWrapperConstants.FUND_FIELD.SHARECLASS;
      break;
  }
  return { primarySort, secondarySort, tertiarySort };
};

export const validateIfProductHeaderSort = (sortOption: string): boolean => {
  return sortOption === ProductCenterTableWrapperConstants.FUND_FIELD.APIR ||
    sortOption ===
      ProductCenterTableWrapperConstants.FUND_FIELD.BASECURRENCYNAME ||
    sortOption === ProductCenterTableWrapperConstants.FUND_FIELD.FUNDNAME ||
    sortOption === ProductCenterTableWrapperConstants.FUND_FIELD.SHARECLASS ||
    sortOption === ProductCenterTableWrapperConstants.FUND_FIELD.VEHICLETYPENAME
    ? true
    : false;
};

/**
 * Resets the sort direction of all table headers.
 * This method either resets the sort direction of all given table headers,
 * or resets the sort direction of all except the provided table header.
 */
export const resetTableSortDirectionHeaders = (
  allBeaconTableHeaderEl: NodeListOf<Element> | undefined,
  tableHeaderEl?: BeaconTableHeader,
) => {
  if (!allBeaconTableHeaderEl) {
    return;
  }
  Array.from(allBeaconTableHeaderEl).forEach((header: BeaconTableHeader) => {
    if (
      (tableHeaderEl && tableHeaderEl.id && header.id !== tableHeaderEl.id) ||
      !tableHeaderEl
    ) {
      header.resetSortedAttr(header);
    }
  });
};

/**
 * Handles the resize event for small breakpoints.
 * This method is triggered when the window is resized and adjusts the table layout
 * to fit small screen sizes.
 *
 * * @returns {boolean} - Returns true if the window width is greater than the small breakpoint value.
 */
export const handleMobileViewSize = (): boolean => {
  return (
    window.innerWidth < getNumericValueFromPx(beaconSystemBreakpointMedium)
  );
};
